import React, { FC } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Button, FormHelperText, Grid, InputAdornment, Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/Icon';
import { LoginContainer } from 'src/components/loginContainer';
import { InputField } from '../../components/input-field';
// import { useAuth } from '../../hooks/use-auth';
import { useMounted } from '../../hooks/use-mounted';
import { LockClosed } from '../../icons/lock-closed';
import VisibilityIcon from '../../icons/visibilityIcon';
import VisibilityIconOff from '../../icons/visibilityIconOff';

const features = [
  'Le mot de passe doit:',
  'avoir au moins 8 caractères',
  'inclure les éléments suivants :',
  ' - un caractère majuscule (A-Z)',
  ' - un caractère minuscule (a-z)',
  ' - un numéro (1-9)',
  ' - un caractère spécial (&”@)'
];
const CreatePasswordPage: FC = () => {
  const mounted = useMounted();
  const noPointer = { cursor: 'pointer' };
  const passwordRegex = RegExp(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&+-.,;=^"`'#_{}()§£/¤:|[\]])[A-Za-z\d@$!%*?&+-.,;=^"`'#_{}()§£/¤:|[\]]{8,}$/
  );
  const [showpass, setShowPass] = React.useState({
    pass: false,

    passConfirm: false
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmationPassword: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .required('Mot de passe obligatoire')
        .matches(passwordRegex, 'Mot de passe invalide'),
      confirmationPassword: Yup.string().oneOf(
        [Yup.ref('password'), null],
        'Mot de passe non identique'
      )
    }),
    onSubmit: async (values, helpers) => {
      try {
        // await login(values.password, values.confirmationPassword);

        if (mounted.current) {
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        }
      } catch (err) {
        console.error(err);
        if (mounted.current) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    }
  });
  const handleClickShowPass = () => {
    setShowPass({
      ...showpass,
      pass: !showpass.pass
    });
  };
  const handleClickShowPassConfirm = () => {
    setShowPass({
      ...showpass,
      passConfirm: !showpass.passConfirm
    });
  };

  return (
    <LoginContainer>
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            mb: 3
          }}
        >
          <Typography color="textPrimary" variant="h4">
            Création de mot de passe
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputField
              error={Boolean(formik.touched.password && formik.errors.password)}
              fullWidth
              helperText={formik.touched.password && formik.errors.password}
              name="password"
              data-testid="password"
              placeholder="Créer votre mot de passe"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type={showpass.pass ? 'text' : 'password'}
              value={formik.values.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockClosed />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    style={{ marginRight: '0.5vh', marginTop: '0.5vh' }}
                  >
                    <IconButton
                      aria-label="toggle password visibility"
                      data-testid="show-password"
                      onClick={handleClickShowPass}
                      style={noPointer}
                    >
                      {showpass.pass ? (
                        <VisibilityIcon style={noPointer} />
                      ) : (
                        <VisibilityIconOff style={noPointer} />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <InputField
              error={Boolean(
                formik.touched.confirmationPassword && formik.errors.confirmationPassword
              )}
              fullWidth
              helperText={formik.touched.confirmationPassword && formik.errors.confirmationPassword}
              name="confirmationPassword"
              data-testid="confirmationPassword"
              placeholder="Confirmer votre mot de passe"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type={showpass.passConfirm ? 'text' : 'password'}
              value={formik.values.confirmationPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockClosed />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    style={{ marginRight: '0.5vh', marginTop: '0.5vh' }}
                  >
                    <IconButton
                      aria-label="toggle password visibility"
                      data-testid="show-confirmed-password"
                      onClick={handleClickShowPassConfirm}
                      style={noPointer}
                    >
                      {showpass.passConfirm ? (
                        <VisibilityIcon style={noPointer} />
                      ) : (
                        <VisibilityIconOff style={noPointer} />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          {formik.errors.submit && (
            <Grid item xs={12}>
              <FormHelperText error>{formik.errors.submit as React.ReactNode}</FormHelperText>
            </Grid>
          )}
          <Grid item xs>
            {features.map((feature) => (
              <Grid fontSize={12} key={feature}>
                {feature}
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <Button
              data-testid="create-password-submit-button"
              color="primary"
              disabled={formik.isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              Créer
            </Button>
          </Grid>
        </Grid>
      </form>
    </LoginContainer>
  );
};
export default CreatePasswordPage;
